/**
 * ST4S reporting access-control — shared registration/approval logic.
 *
 * The pure helpers below (buildRegistrationEntry, notificationFor,
 * canActOnPending, approveUpdate, rejectUpdate) have no DOM, no network and no
 * Neverland, and are used by BOTH registration paths (ConfirmOrgAccessPage and
 * RegisterPage) and the approve/reject UI so the pending/approved split cannot
 * drift (a prior bug came from editing only one path). Design:
 * docs/ST4S/reporting-access-control-design.md §6.2 / §6.3.
 *
 * sendApprovalNotification() (bottom of file) is the ONE impure helper: the
 * two-hop approval-email send for Option A2 (docs/ST4S/notifier-egress-design.md
 * §6). It is still shared by both registration paths for the same no-drift
 * reason, and it NEVER throws so it preserves fire-and-forget semantics. It
 * imports cognito.js LAZILY (dynamic import inside the function) so this module
 * stays pure at load time — importing approval.js must not trigger cognito.js's
 * module-load AWS side effects (which would break the pure-helper test suites).
 */

/**
 * Build the users[] entry for a new registration and decide whether this
 * registrant is the first ACTIVE user of the ClientGroup.
 *
 * First active registrant (no non-removed users yet) -> auto-approved
 * (status 'approved', approvedBy 'first-contact'). Any subsequent registrant
 * -> 'pending' (approvedBy/approvedAt null) awaiting contact-person approval.
 * The email is always lowercased + trimmed.
 *
 * @param {object} params
 * @param {Array}  params.users  Existing ClientGroup.users (may be undefined)
 * @param {string} params.email  Registrant email
 * @param {string} params.nowIso ISO timestamp to stamp on the entry
 * @returns {{ entry: object, isFirst: boolean }}
 */
export function buildRegistrationEntry({ users, email, nowIso }) {
  const existing = Array.isArray(users) ? users : [];
  const isFirst = existing.filter(u => !u.removed).length === 0;
  const normalisedEmail = (email || '').trim().toLowerCase();

  const entry = {
    email: normalisedEmail,
    registeredAt: nowIso,
    removed: false,
    status: isFirst ? 'approved' : 'pending',
    requestedAt: nowIso,
    approvedBy: isFirst ? 'first-contact' : null,
    approvedAt: isFirst ? nowIso : null
  };

  return { entry, isFirst };
}

/**
 * Build the server-side notification payload for a freshly-written entry.
 * First-contact (approved) -> helpdesk FYI; subsequent (pending) -> contact
 * notification. Recipients are derived SERVER-SIDE from the re-read ClientGroup;
 * this payload only carries type/clientGroupId/requester.
 *
 * @param {object} entry         The entry from buildRegistrationEntry
 * @param {string} clientGroupId ClientGroup _id as a hex string ($oid)
 * @returns {{ type: string, clientGroupId: string, requester: string }}
 */
export function notificationFor(entry, clientGroupId) {
  return {
    type: entry.status === 'approved' ? 'first_contact_fyi' : 'new_pending_request',
    clientGroupId,
    requester: entry.email
  };
}

/**
 * Decide whether `actingEmail` may approve/reject a given pending entry.
 * Self-approval is forbidden (a user cannot act on their own pending entry).
 * The acting user must be a contact person or an admin, and the target entry
 * must be a non-removed, pending entry.
 *
 * @param {string} actingEmail  The acting user's email
 * @param {object} pendingEntry The users[] entry being acted on
 * @param {{ isContact?: boolean, isAdmin?: boolean }} roles
 * @returns {boolean}
 */
export function canActOnPending(actingEmail, pendingEntry, { isContact = false, isAdmin = false } = {}) {
  if (!pendingEntry) return false;
  if (pendingEntry.removed) return false;
  if (pendingEntry.status !== 'pending') return false;
  if (!isContact && !isAdmin) return false;

  const acting = (actingEmail || '').trim().toLowerCase();
  const target = (pendingEntry.email || '').trim().toLowerCase();
  if (!acting || acting === target) return false; // self-approval guard

  return true;
}

/**
 * Mongo update object to approve a pending user (positional $ operator; the
 * caller must match the array element via `{ 'users.email': email }`).
 *
 * @param {string} email       The approved user's email
 * @param {string} actingEmail The approver's email (recorded on approvedBy)
 * @param {string} nowIso      ISO timestamp for approvedAt
 */
export function approveUpdate(email, actingEmail, nowIso) {
  return {
    $set: {
      'users.$.status': 'approved',
      'users.$.approvedBy': actingEmail,
      'users.$.approvedAt': nowIso
    }
  };
}

/**
 * Mongo update object to reject a pending user — removes the entry entirely.
 *
 * @param {string} email The rejected user's email
 */
export function rejectUpdate(email) {
  return {
    $pull: { users: { email } }
  };
}

// ---------------------------------------------------------------------------
// Option A2 (signed-ticket split) — two-hop approval-email send.
//
// The notifier's Lambda Function URL. The VPC main Lambda cannot reach SES, so
// Notifications/send now RETURNS an HMAC-signed ticket and the browser relays
// it here (SigV4-signed with the reporting identity-pool creds).
//
// The core infrastructure defines a SINGLE notifier function (ry-ses-notifier,
// no test/prod split — only the API Gateway has test/prod STAGES, not this
// Lambda), so both stage keys point at the same Function URL. The stage-select
// is retained to mirror database.js::getApiStage() in case a separate dev
// notifier is ever introduced. Value is `terraform output notifier_function_url`
// from infrastructure/core (applied 2026-10-14).
const NOTIFIER_FUNCTION_URLS = {
  test: 'https://evg2olllkxl5umbzr3fhz4fa5a0uqpss.lambda-url.ap-southeast-2.on.aws/',
  prod: 'https://evg2olllkxl5umbzr3fhz4fa5a0uqpss.lambda-url.ap-southeast-2.on.aws/'
};

function getNotifierStage() {
  // Mirrors database.js::getApiStage — localhost/dev. -> test, else prod.
  const hostname = (typeof window !== 'undefined' && window.location)
    ? window.location.hostname : '';
  return (hostname === 'localhost' || hostname.includes('dev.')) ? 'test' : 'prod';
}

function getNotifierUrl() {
  return NOTIFIER_FUNCTION_URLS[getNotifierStage()];
}

/**
 * Perform the full two-hop approval-email send and NEVER throw.
 *
 * Hop 1: call Notifications/send (reporting-pool-gated) which re-reads the
 * ClientGroup, derives recipients SERVER-SIDE, and returns an HMAC-signed
 * ticket { ticket: {recipients, subject, body, exp}, sig }.
 * Hop 2: relay that { ticket, sig } VERBATIM to the notifier Lambda Function
 * URL, SigV4-signed with the reporting identity-pool creds (service name
 * 'lambda' for a Function URL), exactly like database.js signs its calls.
 *
 * The relayed body MUST be the server-returned object passed straight through
 * — re-deriving or reordering the ticket in JS would change its bytes and
 * invalidate `sig`, so the notifier would reject it.
 *
 * ANY failure (hop 1 or hop 2) is swallowed/logged — email delivery must never
 * block or break registration. Returns a Promise that always resolves.
 *
 * @param {object} auth          The auth object exposing `auth.db` (database.js)
 * @param {object} entry         The entry from buildRegistrationEntry
 * @param {string} clientGroupId ClientGroup _id as a hex string ($oid)
 */
export async function sendApprovalNotification(auth, entry, clientGroupId) {
  try {
    // Hop 1: reporting-pool-gated derive + sign.
    const resp = await auth.db.request('Notifications/send',
      notificationFor(entry, clientGroupId));

    // Benign no-ticket case (secret unset / no contacts / not signed): nothing
    // to relay.
    if (!resp || !resp.ticket || !resp.sig) return;

    // Hop 2: relay the signed ticket VERBATIM to the notifier Function URL.
    const url = getNotifierUrl();

    // Lazy-load the credential helper so this module's load stays pure (see the
    // file header) — cognito.js runs AWS side effects at module load.
    const { getCredentials } = await import('./cognito.js');

    // ALWAYS obtain fresh credentials from the cognito singleton, not from
    // AWS.config.credentials. At this point the user just completed MFA, so
    // the cognito singleton's session is set (authenticated). But
    // AWS.config.credentials may still hold the UNAUTHENTICATED identity-pool
    // creds from the pre-login theme load — those are valid (not expired) so
    // needsRefresh() returns false, but they carry the UNAUTH role which lacks
    // lambda:InvokeFunctionUrl, causing a 403 AccessDeniedException on the
    // Function URL. getCredentials() rebuilds from the session (→ authenticated
    // role with the grant). Hop 1 (database.js) doesn't hit this because it
    // calls getCredentials() through its own refresh path which checks the
    // cognito singleton's internal credentials cache (cleared on setSession).
    const credentials = await getCredentials();
    AWS.config.credentials = credentials;
    if (!credentials || !credentials.accessKeyId || !credentials.secretAccessKey) {
      throw new Error('Invalid AWS credentials - missing required properties');
    }

    const request = new AWS.HttpRequest(url, 'ap-southeast-2');
    request.method = 'POST';
    // Pass the server response straight through — do NOT rebuild/reorder the
    // ticket (that would invalidate sig).
    request.body = JSON.stringify({ ticket: resp.ticket, sig: resp.sig });
    request.headers['Content-Type'] = 'application/json';
    request.headers['Host'] = new URL(url).host;

    // SigV4 service name for a Lambda Function URL is 'lambda' (not
    // 'execute-api' as for API Gateway).
    const signer = new AWS.Signers.V4(request, 'lambda');
    signer.addAuthorization(credentials, new Date());

    // Browser SigV4 gotcha (the hop-2 403): the signer needs `Host` in the
    // canonical request to compute the signature, but `fetch` FORBIDS setting
    // `Host` and SILENTLY DROPS it. Passing `request.headers` wholesale sends
    // every signed header EXCEPT Host — the signature was computed over a host
    // the browser then omits, so the Function URL's IAM/SigV4 layer recomputes
    // a different signature and rejects the relay with 403 BEFORE the notifier
    // ever runs. API Gateway (hop 1) tolerates this; the Function URL does not.
    // The browser also overrides `User-Agent`, which the SDK signs over too.
    //
    // Fix: forward every signed header EXCEPT the browser-controlled ones
    // (Host, User-Agent) and let the browser set those itself to the exact
    // values that were signed. Critically we COPY HEADERS BY THEIR SIGNED NAME
    // verbatim rather than a hand-written allowlist, because AWS SDK v2 writes
    // the session-token header as LOWERCASE `x-amz-security-token`: a Title-
    // Case allowlist silently drops it and the Function URL then returns 403
    // UnrecognizedClientException ("security token ... is invalid"). (The
    // 'lambda' service also does not emit X-Amz-Content-Sha256 in v2, so an
    // allowlist expecting it would be wrong on two counts.) A Node signing
    // proof against the live Function URL with this exact set returns an
    // application-level {"error":"forbidden"} — PAST the SigV4 layer — whereas
    // the Title-Case allowlist returns UnrecognizedClientException. This
    // mirrors the X-Id-Token/Authorization collision fix (commit 1de67d8c):
    // the signing surface is unforgiving about which signed headers arrive.
    const BROWSER_CONTROLLED_HEADERS = new Set(['host', 'user-agent']);
    const fetchHeaders = {};
    for (const name of Object.keys(request.headers)) {
      if (!BROWSER_CONTROLLED_HEADERS.has(name.toLowerCase())) {
        fetchHeaders[name] = request.headers[name];
      }
    }

    await fetch(url, {
      method: request.method,
      headers: fetchHeaders,
      body: request.body
    });
  } catch (err) {
    // Fire-and-forget: swallow everything so registration is never affected.
    console.error('Approval notification send failed:', err);
  }
}
